import { 
    Box, Typography, Card, Stack, Chip, Button, 
    IconButton, Tooltip, Alert, CircularProgress,
    Table, Sheet, Input,
    Modal,
    ModalDialog,
    ModalClose,
    FormLabel,
    Select,
    Option,
    FormControl
} from "@mui/joy";
import { 
    EditRounded, DeleteRounded, PersonAddRounded, 
    SearchRounded, PersonRounded, PhoneRounded,
    LinkRounded
} from '@mui/icons-material';
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { LayoutContext } from "../context";
import MisReviewDialog from '../components/modals/MisReviewDialog';
import { 
    APIGetClientRequests, 
    APIDeleteClientRequest, 
    APIConvertClientRequestToProfile, 
    APIGetDoctors,
    APIUpdateClientRequestDoctor
} from "../api";

export default function ClientRequestsPage() {
    const { currentDoctor, token, setNewRequestsCount, loadNewRequestsCount } = useContext(LayoutContext);
    const navigate = useNavigate();
    
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [processing, setProcessing] = useState({});
    const [misReviewRequest, setMisReviewRequest] = useState(null);
    
    const [doctors, setDoctors] = useState([]);
    const [editDoctorModal, setEditDoctorModal] = useState({ open: false, request: null });
    const [selectedDoctorId, setSelectedDoctorId] = useState(null);
    const [savingDoctor, setSavingDoctor] = useState(false);

    const isAdmin = (currentDoctor?.type_profile.includes('admin') || currentDoctor?.type_profile?.includes('admin')) || (currentDoctor?.type_profile.includes('supervisor') || currentDoctor?.type_profile?.includes('supervisor'));

    // Проверка прав доступа
    useEffect(() => {
        if (!isAdmin) {
            navigate('/');
        }
    }, [isAdmin, navigate]);

    const loadDoctors = async () => {
        try {
            const response = await APIGetDoctors(token, { all: true, type_profile: 'doctor' });
            setDoctors(response.doctors || []);
        } catch (e) {
            console.error('Ошибка загрузки врачей', e);
        }
    };

    useEffect(() => {
        if (isAdmin) {
            loadRequests();
            loadDoctors();
        }
    }, [isAdmin]);

    // Загрузка заявок
    const loadRequests = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await APIGetClientRequests(token, {
                search: searchTerm
            });
            
            if (response.success) {
                setRequests(response.requests || []);
            } else {
                setError(response.error || "Ошибка при загрузке заявок");
            }
        } catch (error) {
            console.error("Ошибка при загрузке заявок:", error);
            setError("Ошибка при загрузке заявок");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDoctor = async () => {
        if (!editDoctorModal.request) return;
        setSavingDoctor(true);
        try {
            const response = await APIUpdateClientRequestDoctor(
                token,
                editDoctorModal.request.id,
                selectedDoctorId  // null = открепить
            );
            if (response.success) {
                setSuccess('Врач успешно обновлён');
                setEditDoctorModal({ open: false, request: null });
                await loadRequests();
            } else {
                setError(response.error || 'Ошибка при сохранении');
            }
        } catch (e) {
            setError('Ошибка при сохранении врача');
        } finally {
            setSavingDoctor(false);
        }
    };

    // Удаление заявки
    const handleDelete = async (requestId, name) => {
        const isConfirmed = window.confirm(
            `Вы уверены, что хотите удалить заявку "${name}"?\n\nЭто действие нельзя отменить.`
        );
        
        if (!isConfirmed) return;
        
        try {
            setProcessing(prev => ({ ...prev, [requestId]: true }));
            
            const response = await APIDeleteClientRequest(token, requestId);
            
            if (response.success) {
                setSuccess("Заявка успешно удалена");
                await loadRequests();
                loadNewRequestsCount?.();   // обновляем бейдж «Заявки»
            } else {
                setError(response.error || "Ошибка при удалении заявки");
            }
        } catch (error) {
            console.error("Ошибка при удалении заявки:", error);
            setError("Ошибка при удалении заявки");
        } finally {
            setProcessing(prev => ({ ...prev, [requestId]: false }));
        }
    };

    // Конвертация заявки в профиль
    const handleConvertToProfile = async (requestId) => {
        try {
            setProcessing(prev => ({ ...prev, [`convert_${requestId}`]: true }));
            
            const response = await APIConvertClientRequestToProfile(token, requestId);
            
            if (response.success) {
                setSuccess("Заявка успешно конвертирована в профиль пациента");
                await loadRequests();
                // Точный пересчёт бейджа с сервера (вместо ручного «−1»)
                loadNewRequestsCount?.();

            } else {
                setError(response.error || "Ошибка при конвертации заявки");
            }
        } catch (error) {
            console.error("Ошибка при конвертации заявки:", error);
            setError(error?.response?.data?.error || 'Ошибка при обработке заявки');
        } finally {
            setProcessing(prev => ({ ...prev, [`convert_${requestId}`]: false }));
        }
    };

    const handleProcessRequest = (request) => {
        if (request.reason === 'mis_ambiguous') {
            setMisReviewRequest(request);
        } else {
            handleConvertToProfile(request.id);
        }
    };

    const handleMisApproved = async () => {
        setSuccess('Карточка МИС выбрана, заявка подтверждена');
        await loadRequests();
        loadNewRequestsCount?.();
    };

    // Редактирование заявки
    const handleEdit = (request) => {
        // Здесь можно открыть модальное окно для редактирования
        // или перейти на страницу редактирования
        console.log("Редактирование заявки:", request);
        // Временно показываем алерт
        alert(`Редактирование заявки ${request.name}`);
    };

    // Обработчик поиска
    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            loadRequests();
        }
    };

    useEffect(() => {
        if (isAdmin) {
            loadRequests();
        }
    }, [isAdmin]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    if (!isAdmin) {
        return null;
    }

    // Компонент карточки заявки для мобильной версии
    const RequestCard = ({ request }) => {
        const isConverted = request?.status === 'converted' || request?.profile_registration_status === 'approved'
          || (!request?.profile_id && request?.has_existing_profile);
        
        return (
            <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography level="h4" fontSize="md">
                                {request.name}
                            </Typography>
                            {request.reason === 'mis_ambiguous' && (
                                <Chip size="sm" color="warning" variant="soft">Несколько карточек МИС</Chip>
                            )}
                            <Typography level="body2" color="neutral" sx={{ gap: 0.5 }} startDecorator={<PhoneRounded sx={{ fontSize: 14, mr: 0.5 }} />}>
                                Контакт: <Typography component={'span'} color='primary' sx={{ fontWeight: 600 }}>{request.phone || request.email || '—'}</Typography>
                            </Typography>
                            {request.user?.full_name && (
                                <Typography level="body3" color="neutral" startDecorator={<PersonRounded sx={{ fontSize: 14, mr: 0.5 }} />}>
                                    Пользователь: {request.user.full_name}
                                </Typography>
                            )}
                        </Box>
                        
                        <Chip 
                            size="sm" 
                            color={isConverted ? "success" : "neutral"}
                            variant="soft"
                        >
                            {isConverted ? "Конвертирован" : "Новый"}
                        </Chip>
                    </Stack>

                    <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Typography level="body2" sx={{ flex: 1 }}>
                            Врач: <b>{request.doctor?.name || '—'} {request.doctor?.specialization?.length > 0 && (<Chip component={'span'} level="body2" color="primary">({request.doctor?.specialization.map(s => s.name).join(', ')})</Chip>)}</b>
                        </Typography>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<LinkRounded />}
                            disabled={request.reason === 'mis_ambiguous'}
                            onClick={() => {
                                setSelectedDoctorId(request.doctor?.id || null);
                                setEditDoctorModal({ open: true, request });
                            }}
                        >
                            Изменить врача
                        </Button>
                    </Stack>

                    <Stack direction={'column'} sx={{ gap: 1 }}>
                        <Button
                            size="md"
                            variant="outlined"
                            color="danger"
                            startDecorator={<DeleteRounded />}
                            loading={processing[request.id]}
                            disabled={request.reason === 'mis_ambiguous'}
                            onClick={() => handleDelete(request.id, request.name)}
                            fullWidth
                        >
                            Удалить
                        </Button>

                        <Button
                            size="md"
                            variant={isConverted ? "soft" : "solid"}
                            color={isConverted ? "success" : "primary"}
                            startDecorator={<PersonAddRounded />}
                            loading={processing[`convert_${request.id}`]}
                            onClick={() => handleProcessRequest(request)}
                            disabled={isConverted}
                        >
                            {isConverted ? "Обработана" : request.reason === 'mis_ambiguous'
                                ? 'Уточнить карточку МИС' : 'Подтвердить заявку'}
                        </Button>
                    </Stack>
                </Stack>
            </Card>
        );
    };

    // Компонент строки таблицы для десктопной версии
    const RequestRow = ({ request }) => {
        const isConverted = request?.status === 'converted' || request?.profile_registration_status === 'approved'
          || (!request?.profile_id && request?.has_existing_profile);
        
        return (
            <tr>
                <td>
                    <Typography level="body1">{request.name}</Typography>
                    {request.reason === 'mis_ambiguous' && (
                        <Chip size="sm" color="warning" variant="soft">Несколько карточек МИС</Chip>
                    )}
                </td>
                <td>
                    <Typography level="body2">{request.phone || request.email || "—"}</Typography>
                </td>
                <td>
                    <Typography level="body2">
                        {request.user?.full_name || "—"}
                    </Typography>
                </td>
                <td>
                    <Chip 
                        size="sm" 
                        color={isConverted ? "success" : "neutral"}
                        variant="soft"
                    >
                        {isConverted ? "Конвертирован" : "Новый"}
                    </Chip>
                </td>
                <td>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography level="body2">
                            {request.doctor?.name || '—'}
                        </Typography>
                        <Tooltip title="Изменить врача">
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                disabled={request.reason === 'mis_ambiguous'}
                                onClick={() => {
                                    setSelectedDoctorId(request.doctor?.id || null);
                                    setEditDoctorModal({ open: true, request });
                                }}
                            >
                                <LinkRounded fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </td>
                <td>
                    <Typography level="body2">
                        {new Date(request.date_create).toLocaleDateString()}
                    </Typography>
                </td>
                <td>
                    <Stack direction="row" spacing={1}>
                        {/* <Tooltip title="Редактировать">
                            <IconButton
                                size="sm"
                                variant="outlined"
                                color="neutral"
                                onClick={() => handleEdit(request)}
                            >
                                <EditRounded />
                            </IconButton>
                        </Tooltip> */}
                        
                        <Tooltip title="Удалить">
                            <IconButton
                                size="sm"
                                variant="outlined"
                                color="danger"
                                loading={processing[request.id]}
                                disabled={request.reason === 'mis_ambiguous'}
                                onClick={() => handleDelete(request.id, request.name)}
                            >
                                <DeleteRounded />
                            </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={isConverted ? 'Заявка обработана' : request.reason === 'mis_ambiguous'
                            ? 'Проверить пациента и выбрать карточку МИС' : 'Подтвердить заявку'}>
                            <Button
                                size="sm"
                                variant={isConverted ? "soft" : "solid"}
                                color={isConverted ? "success" : "primary"}
                                startDecorator={<PersonAddRounded />}
                                loading={processing[`convert_${request.id}`]}
                                onClick={() => handleProcessRequest(request)}
                                disabled={isConverted}
                            >
                                {isConverted ? 'Обработана' : request.reason === 'mis_ambiguous'
                                    ? 'Уточнить карточку МИС' : 'Подтвердить заявку'}
                            </Button>
                        </Tooltip>
                    </Stack>
                </td>
            </tr>
        );
    };

    return (
        <>
            <MisReviewDialog request={misReviewRequest} token={token}
                onClose={() => setMisReviewRequest(null)} onApproved={handleMisApproved} />
            {/* Модальное окно выбора врача для заявки */}
            <Modal open={editDoctorModal.open} onClose={() => setEditDoctorModal({ open: false, request: null })} sx={{ zIndex: 10021 }}>
                <ModalDialog size="sm" sx={{ borderRadius: 20 }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>
                        Врач для заявки: <b>{editDoctorModal.request?.name}</b>
                    </Typography>
                    <FormControl>
                        <FormLabel>Выберите врача</FormLabel>
                        <Select
                            value={selectedDoctorId}
                            onChange={(_, v) => setSelectedDoctorId(v)}
                            placeholder="Без врача"
                            slotProps={{ listbox: { sx: { zIndex: 10022, maxHeight: 300, overflow: 'auto' } } }}
                        >
                            <Option value={null}>— Без врача —</Option>
                            {doctors.map(d => (
                                <Option key={d.id} value={d.id}>
                                    {d.name}
                                    {d.specialization?.length > 0 && (<Typography component={'span'} level="body2" color="neutral">({d.specialization.map(s => s.name).join(', ')})</Typography>)}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button variant="outlined" color="neutral" fullWidth
                            onClick={() => setEditDoctorModal({ open: false, request: null })}>
                            Отмена
                        </Button>
                        <Button variant="solid" color="primary" fullWidth
                            loading={savingDoctor} onClick={handleSaveDoctor}>
                            Сохранить
                        </Button>
                    </Stack>
                </ModalDialog>
            </Modal>

            <Box sx={{ display: 'flex' }}>
                <Box
                    component="main"
                    className="MainContent"
                    sx={{
                        px: { xs: 2, md: 6 },
                        pt: 2,
                        pb: { xs: 2, sm: 2, md: 3 },
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            mb: 1,
                            gap: 1,
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'start', sm: 'center' },
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography level="h2" component="h1">
                            Заявки клиентов
                        </Typography>
                        
                        <Input
                            placeholder="Поиск по имени или телефону..."
                            startDecorator={<SearchRounded />}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleSearch}
                            sx={{ width: '100%' }}
                            endDecorator={
                                <Button
                                    variant="soft"
                                    onClick={loadRequests}
                                >
                                    Найти
                                </Button>
                            }
                        />
                    </Box>

                    {error && (
                        <Alert color="danger" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert color="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : requests.length > 0 ? (
                        <>
                            {/* Десктопная версия (таблица) */}
                            <Sheet 
                                variant="outlined" 
                                sx={{ 
                                    display: { xs: 'none', md: 'block' },
                                    borderRadius: 'sm',
                                    overflow: 'auto'
                                }}
                            >
                                <Table 
                                    hoverRow 
                                    sx={{ 
                                        tableLayout: 'auto',
                                        minWidth: 800
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={{ width: '20%' }}>Имя</th>
                                            <th style={{ width: '15%' }}>Телефон</th>
                                            <th style={{ width: '20%' }}>Пользователь Telegram</th>
                                            <th style={{ width: '15%' }}>Статус</th>
                                            <th style={{ width: '15%' }}>Врач</th>
                                            <th style={{ width: '15%' }}>Дата создания</th>
                                            <th style={{ width: '15%' }}>Действия</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {requests.map(request => (
                                            <RequestRow key={request.id} request={request} />
                                        ))}
                                    </tbody>
                                </Table>
                            </Sheet>

                            {/* Мобильная версия (карточки) */}
                            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                {requests.map(request => (
                                    <RequestCard key={request.id} request={request} />
                                ))}
                            </Box>
                        </>
                    ) : (
                        <Alert color="neutral" sx={{ mt: 2 }}>
                            Заявки не найдены
                        </Alert>
                    )}
                </Box>
            </Box>
        </>
    );
}